import { baseClient } from "./baseRequest";

export const NEW_ARTISTS_SUBMISSIONS_QUERY_KEY = [
  "new-artists-contact-submissions",
] as const;

export enum NewArtistStatus {
  Pending = "pending",
  Approved = "approved",
  Rejected = "rejected",
  Clarification = "clarification",
}

// The statuses an admin can set via a review action (PENDING is the initial
// state only, never a review outcome).
export type NewArtistReviewStatus =
  | NewArtistStatus.Approved
  | NewArtistStatus.Rejected
  | NewArtistStatus.Clarification;

export type NewArtistReviewDetails = {
  status: NewArtistStatus;
  reviewedAt: number | null;
  reviewedBy: string | null;
  notes: string | null;
  emailSentAt: number | null;
};

export type StatusChipColor =
  | "default"
  | "success"
  | "error"
  | "info"
  | "warning";

// Single source of truth for how each status renders (label + chip color),
// shared by the table and the review modal so they can never drift.
export const NEW_ARTIST_STATUS_META: Record<
  NewArtistStatus,
  { label: string; color: StatusChipColor }
> = {
  [NewArtistStatus.Pending]: { label: "Pending", color: "warning" },
  [NewArtistStatus.Approved]: { label: "Approved", color: "success" },
  [NewArtistStatus.Rejected]: { label: "Rejected", color: "error" },
  [NewArtistStatus.Clarification]: { label: "Clarification", color: "info" },
};

// Collapses a null/undefined or out-of-enum status (e.g. legacy data or a
// backend status added ahead of the frontend) to a known enum value. Used for
// both the chip and the sort/filter key so display and filtering always agree.
export const normalizeNewArtistStatus = (
  status?: string | null,
): NewArtistStatus =>
  NEW_ARTIST_STATUS_META[status as NewArtistStatus]
    ? (status as NewArtistStatus)
    : NewArtistStatus.Pending;

export const getNewArtistStatusMeta = (status?: string | null) =>
  NEW_ARTIST_STATUS_META[normalizeNewArtistStatus(status)];

export type NewArtistsContactSubmission = {
  id: string;
  fullName: string;
  email: string;
  portfolioUrl: string;
  message: string | null;
  createdAt?: string;
  reviewDetails?: NewArtistReviewDetails;
};

export type ReviewNewArtistInput = {
  id: string;
  status: NewArtistReviewStatus;
  sendEmail: boolean;
  notes?: string | null;
};

export type NewArtistsApiClient = {
  getSubmissions: () => Promise<NewArtistsContactSubmission[]>;
  reviewSubmission: (input: ReviewNewArtistInput) => Promise<void>;
};

const newArtistsClient: NewArtistsApiClient = {
  getSubmissions: async () => {
    return baseClient.get<NewArtistsContactSubmission[]>({
      url: "/admin/new-artists/contact",
    });
  },
  reviewSubmission: async ({ id, status, sendEmail, notes }) => {
    await baseClient.put<void>({
      url: `/admin/new-artists/contact/${id}/review`,
      body: { status, sendEmail, notes },
    });
  },
};

export default newArtistsClient;
